import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators } from '@nkr/store/index';
import { IBaseResponse, IListItem, IMicrositeRecipient } from '@nkr/models/index';
import { Button, Col, FormControl, FormGroup, Glyphicon, Panel, Row } from 'react-bootstrap';
import MicrositeHistory from './microsite-history';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface IProps {
    micrositeId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean) => Promise<IMicrositeResponse>,
    reactivate: (id: number, editKey: string, statusId: number) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    recipient: IMicrositeRecipient,
    previousLocation: string,
    statusList: IListItem[],
    statusId
}

class MicrositeReactivate extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        previousLocation: '',
        statusList: [{ id: 5, description: 'Starter Site - Active' }],
        statusId: 5
    }

    componentDidMount = () => {
        let previousLocation = this.props.isAdmin ? `/admin/microsite/${this.props.previousUrl}` : `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;

        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            this.props.getMicrosite(this.props.micrositeId, false)
                .then(response => {
                    if (response.data.success) {
                        let recipient = response.data.results;

                        this.setState({
                            recipient,
                            previousLocation
                        }, () => {
                            if (recipient.canBeActiveOrCustom) {
                                
                                let statusList = this.state.statusList;
                                statusList.push({ id: 100, description: 'Custom Site - Active' });

                                this.setState({
                                    statusList
                                })
                            }
                        });
                    }

                    this.props.hideLoading();
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                });
        });
    }

    handleApprove = () => {
        let { id, editKey } = this.state.recipient;

        let { statusId } = this.state;

        this.props.showLoading('Approving microsite, one moment please...').then(() => {
            this.props.reactivate(id, editKey, statusId)
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Microsite reactivated successfully, going back to previous page', {
                            onClose: () => {
                                window.location.href = this.state.previousLocation;
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error('Unable to reactivate the microsite, the following error has been recorded: ' + error.message);
                });
        });
    }

    handleCancel = () => {
        window.location.href = this.state.previousLocation;
    }

    handleStatusChange = (e: React.FormEvent<HTMLSelectElement & FormControl>) => {
        let statusId = e.currentTarget.value;

        this.setState({
            statusId
        })
    }

    render = () => {
        if (this.state.recipient == null)
            return <div>One moment please...</div>;

        const { submissionLastIssue, fullName, logs } = this.state.recipient;

        return (
            <>
                <div className='container'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
                        <h1 style={{ margin: 0 }}>Reactivate Microsite</h1>
                        <div className='dropdown pull-right'>
                            <Button bsStyle='link' onClick={this.handleCancel}>Return to Previous List</Button>
                        </div>
                    </div>

                    {submissionLastIssue != '' && (
                        <Row>
                            <Col xs={12}>
                                <MicrositeHistory
                                    name={fullName}
                                    logs={logs}
                                    isExpanded={false}
                                />
                            </Col>
                        </Row>
                    )}

                    <Panel style={{ marginBottom: 50 }}>
                        <Panel.Body style={{ padding: 25 }}>
                            <div style={{ marginBottom: 30 }}>Please confirm that you wish to reactivate this patient's microsite...</div>

                            <Row style={{ display: 'flex', alignItems: 'center' }}>
                                <Col xs={12} sm={4} smOffset={1} className='text-center'>
                                    <Panel>
                                        <Panel.Heading>Current Status</Panel.Heading>
                                        <Panel.Body>
                                            <span style={{ fontWeight: 600 }}>{this.state.recipient.currentStatusDescription}</span>
                                        </Panel.Body>
                                    </Panel>
                                </Col>
                                <Col xs={12} sm={1} className='text-center'><Glyphicon glyph='forward' /></Col>
                                <Col xs={12} sm={4} className='text-center'>
                                    <Panel bsStyle='success'>
                                        <Panel.Heading>New Status</Panel.Heading>
                                        <Panel.Body>
                                            <FormControl
                                                componentClass='select'
                                                name='statusId'
                                                value={this.state.statusId}
                                                onChange={this.handleStatusChange}
                                            >
                                                {this.state.statusList.map((x) => 
                                                    <option value={x.id}>{x.description}</option>
                                                )}        
                                            </FormControl>
                                        </Panel.Body>
                                    </Panel>
                                </Col>
                            </Row>
                        </Panel.Body>
                    </Panel>

                    <FormGroup>
                        <Col xs={12} className='text-center'>
                            <Button bsStyle='primary' bsSize='lg' onClick={this.handleApprove}>Reactivate</Button>
                            <Button bsStyle='link' name='cancel' onClick={this.handleCancel}>Cancel Reactivation</Button>
                        </Col>
                    </FormGroup>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrosite: (id, forCoach) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach));
        },
        reactivate: (id, editKey, statusId) => {
            return dispatch(MicrositeActionCreators.reactivate(id, editKey, statusId));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeReactivate);